import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProject } from '../services/api';
// OpWMP Steps
import Step1ProjectDetails from '../components/wizard/Step1ProjectDetails';
import Step2DevelopmentParameters from '../components/wizard/Step2DevelopmentParameters';
import Step3WasteStreams from '../components/wizard/Step3WasteStreams';
import Step4BinCalculations from '../components/wizard/Step4BinCalculations';
import Step5ManagementMeasures from '../components/wizard/Step5ManagementMeasures';
import Step6RefinementsRecommendations from '../components/wizard/Step6RefinementsRecommendations';
import Step7DocumentUploads from '../components/wizard/Step7DocumentUploads';
import Step8ReviewPayment from '../components/wizard/Step8ReviewPayment';
// D&CWMP Steps
import DCWMPStep2DevelopmentScope from '../components/wizard/DCWMPStep2DevelopmentScope';
import DCWMPStep3DemolitionPhase from '../components/wizard/DCWMPStep3DemolitionPhase';
import DCWMPStep4ConstructionPhase from '../components/wizard/DCWMPStep4ConstructionPhase';
import DCWMPStep5WasteEstimates from '../components/wizard/DCWMPStep5WasteEstimates';
// CMP Steps
import CMPStep2EnvironmentalControls from '../components/wizard/CMPStep2EnvironmentalControls';
import CMPStep3SitePlanning from '../components/wizard/CMPStep3SitePlanning';
import CMPStep4OperationalManagement from '../components/wizard/CMPStep4OperationalManagement';
import CMPStep5ConstructionWaste from '../components/wizard/CMPStep5ConstructionWaste';

const ProjectWizard = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Determine plan type
  const isDCWMP = project?.plan_type === 'demolition_construction_wmp';
  const isCMP = project?.plan_type === 'construction_management_plan';
  
  // Define steps based on plan type
  const opwmpSteps = [
    { number: 1, name: 'Project Details' },
    { number: 2, name: 'Development Parameters' },
    { number: 3, name: 'Waste Streams' },
    { number: 4, name: 'General Waste Planning Guidance' },
    { number: 5, name: 'Waste Calculations' },
    { number: 6, name: 'Refinements' },
    { number: 7, name: 'Supporting Docs' },
    { number: 8, name: 'Review & Payment' }
  ];
  
  const dcwmpSteps = [
    { number: 1, name: 'Project Details' },
    { number: 2, name: 'Development Scope' },
    { number: 3, name: 'Demolition Phase' },
    { number: 4, name: 'Construction Phase' },
    { number: 5, name: 'Waste Estimates' },
    { number: 6, name: 'Supporting Docs' },
    { number: 7, name: 'Review & Payment' }
  ];

  const cmpSteps = [
    { number: 1, name: 'Project Details' },
    { number: 2, name: 'Environmental Controls' },
    { number: 3, name: 'Site Planning' },
    { number: 4, name: 'Operational Management' },
    { number: 5, name: 'Construction Waste' },
    { number: 6, name: 'Supporting Docs' },
    { number: 7, name: 'Review & Payment' }
  ];
  
  const steps = isCMP ? cmpSteps : (isDCWMP ? dcwmpSteps : opwmpSteps);
  const totalSteps = isCMP ? 7 : (isDCWMP ? 7 : 8);

  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject();
    } else {
      setLoading(false);
    }
  }, [projectId]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const loadProject = async () => {
    try {
      const data = await getProject(projectId);
      setProject(data);
      setCurrentStep(data.current_step || 1);
      // Load selected add-ons from project if exists
      if (data.selected_addons && data.selected_addons.length > 0) {
        setSelectedAddons(data.selected_addons);
      } else if (data.rfi_addon) {
        // Backwards compatibility: convert old rfi_addon to new format
        setSelectedAddons(['rfi_response']);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = (updatedProject) => {
    if (updatedProject) {
      setProject(updatedProject);
    }
    // Use the current_step from the server response if available, otherwise increment
    const nextStep = updatedProject?.current_step || (currentStep + 1);
    if (nextStep <= totalSteps) {
      setCurrentStep(nextStep);
    }
    // Reload project to get latest data
    loadProject();
  };

  const handleStepBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBackToDashboard = () => {
    // Show confirmation if user is mid-wizard (not on step 1 or completed)
    const finalStep = isCMP ? 7 : (isDCWMP ? 7 : 8);
    if (currentStep > 1 && currentStep < finalStep) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? Your progress has been saved, but you may want to complete your plan first.'
      );
      if (!confirmed) return;
    }
    navigate('/dashboard');
  };

  // Get display title based on plan type
  const getPlanTypeTitle = () => {
    if (isCMP) return 'CMP';
    if (isDCWMP) return 'D&CWMP';
    return 'OpWMP';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-blue-600 text-xl">Loading...</div>
      </div>
    );
  }

  // Render step content based on plan type and current step
  const renderStepContent = () => {
    if (isCMP) {
      // CMP Steps (7 total)
      const effectiveStep = currentStep > 7 ? 7 : currentStep;
      switch (effectiveStep) {
        case 1:
          return <Step1ProjectDetails project={project} onComplete={handleStepComplete} />;
        case 2:
          return <CMPStep2EnvironmentalControls project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 3:
          return <CMPStep3SitePlanning project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 4:
          return <CMPStep4OperationalManagement project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 5:
          return <CMPStep5ConstructionWaste project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 6:
          return <Step7DocumentUploads project={project} onComplete={handleStepComplete} onBack={handleStepBack} isCMP={true} />;
        case 7:
          return <Step8ReviewPayment project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} isCMP={true} />;
        default:
          return <Step8ReviewPayment project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} isCMP={true} />;
      }
    } else if (isDCWMP) {
      // D&CWMP Steps (7 total)
      // Handle case where step might be 8 due to legacy data - treat as step 7
      const effectiveStep = currentStep > 7 ? 7 : currentStep;
      switch (effectiveStep) {
        case 1:
          return <Step1ProjectDetails project={project} onComplete={handleStepComplete} />;
        case 2:
          return <DCWMPStep2DevelopmentScope project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 3:
          return <DCWMPStep3DemolitionPhase project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 4:
          return <DCWMPStep4ConstructionPhase project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 5:
          return <DCWMPStep5WasteEstimates project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 6:
          return <Step7DocumentUploads project={project} onComplete={handleStepComplete} onBack={handleStepBack} isDCWMP={true} />;
        case 7:
          return <Step8ReviewPayment project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} isDCWMP={true} />;
        default:
          return <Step8ReviewPayment project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} isDCWMP={true} />;
      }
    } else {
      // OpWMP Steps (original)
      switch (currentStep) {
        case 1:
          return <Step1ProjectDetails project={project} onComplete={handleStepComplete} />;
        case 2:
          return <Step2DevelopmentParameters project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 3:
          return <Step3WasteStreams project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 4:
          return <Step4BinCalculations project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 5:
          return <Step5ManagementMeasures project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 6:
          return <Step6RefinementsRecommendations project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} setSelectedAddons={setSelectedAddons} />;
        case 7:
          return <Step7DocumentUploads project={project} onComplete={handleStepComplete} onBack={handleStepBack} />;
        case 8:
          return <Step8ReviewPayment project={project} onComplete={handleStepComplete} onBack={handleStepBack} selectedAddons={selectedAddons} />;
        default:
          return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/plangen-consulting-logo.png" 
                alt="PlanGen Consulting" 
                className="h-8 w-auto"
              />
              {project && (
                <span className="ml-2 text-gray-600">| {project.project_name}</span>
              )}
              {isDCWMP && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                  Demolition & Construction
                </span>
              )}
              {isCMP && (
                <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                  Construction Management
                </span>
              )}
            </div>
            <div className="flex items-center space-x-6">
              <button
                onClick={handleBackToDashboard}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Spacer for fixed header */}
      <div className="h-16"></div>

      {/* Progress Steps - Only show when project exists (not on new project creation) */}
      {project && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Progress">
              <ol className="flex items-start justify-between">
                {steps.map((step, index) => (
                  <li key={step.number} className="relative flex-1">
                    {index !== 0 && (
                      <div
                        className="absolute top-4 left-0 -ml-px h-0.5 w-full -translate-y-1/2"
                        style={{
                          backgroundColor: currentStep > step.number ? (isDCWMP ? '#d97706' : '#0066CC') : '#E5E7EB'
                        }}
                      />
                    )}
                    <div className="relative flex flex-col items-center group">
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium ${
                          currentStep > step.number
                            ? isDCWMP ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'
                            : currentStep === step.number
                            ? isDCWMP ? 'bg-amber-600 text-white ring-4 ring-amber-100' : 'bg-blue-600 text-white ring-4 ring-blue-100'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {currentStep > step.number ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          step.number
                        )}
                      </span>
                      <span className="mt-2 text-xs font-medium text-gray-600 text-center w-20">
                        {step.name}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      )}

      {/* Step Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderStepContent()}
      </main>
    </div>
  );
};

export default ProjectWizard;
